import { WidgetThumbnail, cx } from '@flexbyt/widget-preview';
import { createFileRoute } from '@tanstack/react-router';
import { PageHero } from '../components/ui/hero.js';
import { ToolsScene } from '../components/mascot/scenes.js';
import { ButtonLink, SiteLink } from '../components/ui/button.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Eyebrow, Heading, Mark, Text } from '../components/ui/type.js';
import {
    WIDGET_BUILDER_PATH,
    WIDGET_CONTENT,
    generatorPath,
} from '../content/widgets.js';
import { breadcrumbSchema, jsonLd, seo } from '../lib/seo.js';

/**
 * The free tools directory.
 *
 * Short, and deliberately so. It exists to be a hub — one page linking the six
 * generators plus the general builder, so they are reachable from the footer
 * and from each other — and its job is done in one screen. A directory that
 * needs scrolling is a directory that should have been a list.
 *
 * Each entry shows the layout's own sketch rather than an icon, because the
 * difference between these tools is entirely the shape they produce.
 */
export const Route = createFileRoute('/tools/')({
  head: () => {
    const { meta, links } = seo({
      title: 'Free review widget tools',
      description:
        'Build a review widget for your website free — carousel, masonry, marquee, grid, list or popup. No account needed to try one.',
      path: '/tools',
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Free tools', path: '/tools' },
            ])
          ),
        },
      ],
    };
  },
  component: Tools,
});

function Tools() {
  return (
    <>
      <PageHero
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Free tools', path: '/tools' },
        ]}
        art={<ToolsScene />}
      >
        <Measure>
          <Eyebrow>Free tools</Eyebrow>
          <Heading level={1} size="hero" className="mt-4">
            Build a review widget, <span className="whitespace-nowrap"><Mark>free</Mark>.</span>
          </Heading>
          <Text size="lead" className="mt-5">
            Real widgets, built with the same code that runs on customers&rsquo;
            websites. Style one however you like — you only need an account when
            you want to put it on your site, and what you built comes with you.
          </Text>

          <div className="mt-8">
            <ButtonLink to={WIDGET_BUILDER_PATH} size="lg">
              Open the widget builder
            </ButtonLink>
          </div>
        </Measure>
      </PageHero>

      <Section aria-labelledby="generators-heading">
        <Heading level={2} id="generators-heading" size="title">
          Or start from a layout
        </Heading>

        <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {WIDGET_CONTENT.map((widget) => (
            <li key={widget.slug}>
              <SiteLink
                to={generatorPath(widget)}
                className={cx(
                  'group flex h-full flex-col rounded-xl bg-white p-4 ring-1 ring-ink-150',
                  'transition-colors duration-[var(--duration-fast)]',
                  'hover:ring-ink-300'
                )}
              >
                <WidgetThumbnail type={widget.type} />
                <span className="mt-4 block text-body-sm font-medium text-ink-950">
                  {widget.seoName} generator
                </span>
                <span className="mt-1.5 block text-caption text-ink-500">
                  {widget.bestFor}
                </span>
              </SiteLink>
            </li>
          ))}
        </ul>
      </Section>

      <Section tone="ink" size="base">
        <Measure>
          <Heading level={2} className="text-white">
            Nothing to lose by trying.
          </Heading>
          <Text className="mt-4 text-white/70">
            The builder needs no account, no card and no email. Sign up when you
            have something you want on your website — everything you changed
            comes with you.
          </Text>
          <div className="mt-8">
            <ButtonLink to={WIDGET_BUILDER_PATH} variant="inverse" size="lg">
              Open the widget builder
            </ButtonLink>
          </div>
        </Measure>
      </Section>
    </>
  );
}
